import {GearIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'

import {DiffCompactLinesPopover} from './components/DiffCompactLinesPopover'
import {
  DiffLinePresentationToggles,
  type DiffLinePresentationTogglesProps,
} from './components/DiffLinePresentationToggles'
import {DiffViewPreferenceToggle} from './components/DiffViewPreferenceToggle'
import {DiffCommentsPreferenceToggle} from './components/DiffCommentsPreferenceToggle'
import {useAnalytics} from '@github-ui/use-analytics'
import type {UserNotice} from './types'

const ALL_DIFF_VIEW_SETTINGS = ['layout', 'comments', 'whitespace', 'line-spacing'] as const

export type AvailableDiffViewSettings = (typeof ALL_DIFF_VIEW_SETTINGS)[number]

export type DiffViewSettingsMenuContentProps = {
  additionalPreferences?: React.ReactNode
  reloadOnSplitPreferenceChange?: boolean
  reloadOnWhitespaceChange?: boolean
  enabledDiffViewSettings?: readonly AvailableDiffViewSettings[]
} & Pick<DiffLinePresentationTogglesProps, 'defaultHideWhitespace' | 'onUpdateWhitespace'>

/**
 * The menu content for diff view settings, without the ActionMenu wrapper.
 * This can be used standalone in an existing ActionList, or wrapped in DiffViewSettings.
 */
export function DiffViewSettingsMenuContent({
  additionalPreferences,
  defaultHideWhitespace,
  enabledDiffViewSettings = ALL_DIFF_VIEW_SETTINGS,
  onUpdateWhitespace,
  reloadOnSplitPreferenceChange = false,
  reloadOnWhitespaceChange = false,
}: DiffViewSettingsMenuContentProps) {
  const showLayoutDivider = enabledDiffViewSettings.includes('layout') && enabledDiffViewSettings.includes('comments')

  const showDiffLinePresentationToggles =
    enabledDiffViewSettings.includes('line-spacing') || enabledDiffViewSettings.includes('whitespace')

  const showPresentationDivider =
    showDiffLinePresentationToggles &&
    (enabledDiffViewSettings.includes('comments') || enabledDiffViewSettings.includes('layout'))

  return (
    <>
      {enabledDiffViewSettings.includes('layout') && (
        <DiffViewPreferenceToggle reloadOnChange={reloadOnSplitPreferenceChange} />
      )}

      {showLayoutDivider && <ActionList.Divider />}

      {enabledDiffViewSettings.includes('comments') && <DiffCommentsPreferenceToggle />}

      {showPresentationDivider && <ActionList.Divider />}

      {showDiffLinePresentationToggles ? (
        <>
          <DiffLinePresentationToggles
            defaultHideWhitespace={defaultHideWhitespace}
            lineSpacingPreferenceAvailable={enabledDiffViewSettings.includes('line-spacing')}
            whitespacePreferenceAvailable={enabledDiffViewSettings.includes('whitespace')}
            onUpdateWhitespace={onUpdateWhitespace}
            reloadOnChange={reloadOnWhitespaceChange ? () => window.location.reload() : undefined}
          />
        </>
      ) : null}
      {additionalPreferences}
    </>
  )
}

export type DiffViewSettingsProps = {
  additionalPreferences?: React.ReactNode
  invisible?: boolean
  reloadOnSplitPreferenceChange?: boolean
  reloadOnWhitespaceChange?: boolean
  small?: boolean
  enabledDiffViewSettings?: readonly AvailableDiffViewSettings[]
  userNotices?: Array<UserNotice<string>>
} & Pick<DiffLinePresentationTogglesProps, 'defaultHideWhitespace' | 'onUpdateWhitespace'>

export function DiffViewSettings({
  additionalPreferences,
  invisible = true,
  defaultHideWhitespace,
  enabledDiffViewSettings = ALL_DIFF_VIEW_SETTINGS,
  onUpdateWhitespace,
  reloadOnSplitPreferenceChange = false,
  reloadOnWhitespaceChange = false,
  small,
  userNotices,
}: DiffViewSettingsProps) {
  const {sendAnalyticsEvent} = useAnalytics()

  const handleDiffSettingsClick = () => {
    sendAnalyticsEvent('diff_view.settings.open', 'DIFF_VIEW_SETTINGS_BUTTON')
  }

  return (
    <>
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            aria-label="Open diff view settings"
            icon={GearIcon}
            size={small ? 'small' : 'medium'}
            variant={invisible ? 'invisible' : 'default'}
            onClick={handleDiffSettingsClick}
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay>
          <ActionList>
            <DiffViewSettingsMenuContent
              additionalPreferences={additionalPreferences}
              defaultHideWhitespace={defaultHideWhitespace}
              enabledDiffViewSettings={enabledDiffViewSettings}
              onUpdateWhitespace={onUpdateWhitespace}
              reloadOnSplitPreferenceChange={reloadOnSplitPreferenceChange}
              reloadOnWhitespaceChange={reloadOnWhitespaceChange}
            />
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      <DiffCompactLinesPopover userNotices={userNotices} />
    </>
  )
}

try{ DiffViewSettingsMenuContent.displayName ||= 'DiffViewSettingsMenuContent' } catch {}
try{ DiffViewSettings.displayName ||= 'DiffViewSettings' } catch {}